import { useState } from "react";
import {
  NotificationBadge,
  NotificationInbox,
  NotificationPreferences,
} from "@sfc/notifications/react";
import { type InboxItem } from "@sfc/notifications/contracts";
import {
  Button,
  ToggleGroup,
  ToggleGroupItem,
  Dialog,
  DialogTrigger,
  ScrollableDialogContent,
  DialogBody,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@sfc/ui";
import { trpc } from "@client/trpc";
import { pinnedActingAsTrpcOptions } from "@client/trpcLinks";
import { useImpersonationRefValue } from "@client/impersonationRef";
import { useMutationAction } from "@client/actions/useMutationAction";
import { confirmQuery } from "@client/actions/defineAction";
import { QueryContent } from "@client/components/admin-tools/QueryContent";

type Cursor = { createdAt: string; id: string };

type Pinned = ReturnType<typeof pinnedActingAsTrpcOptions>;

/**
 * `viewerId` is the effective user the bell shows, `actingAs` the impersonation
 * that produces it (null = the real signed-in user). Every request here is
 * pinned to `actingAs`, and the poll pauses whenever the live impersonation
 * ref no longer matches: the header remounts the bell for the new identity a
 * render later, and until then the old bell must not ask about a viewer the
 * server will refuse (routes/notifications.ts, viewer-mismatch FORBIDDEN).
 */
export function NotificationCenter({
  viewerId,
  actingAs,
}: {
  viewerId: string;
  actingAs: string | null;
}) {
  const [open, setOpen] = useState(false);
  const pinned = pinnedActingAsTrpcOptions(actingAs);
  const current = useImpersonationRefValue() === actingAs;
  const count = trpc.getNotificationUnreadCount.useQuery(
    { viewerId },
    { ...pinned, enabled: current, refetchInterval: current ? 30000 : false },
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <NotificationBadge count={count.data} unavailable={count.isError} />
        }
      />
      <ScrollableDialogContent className="max-h-[85dvh] min-w-0 p-4 sm:max-w-xl sm:p-6">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="heading-md">Notifications</DialogTitle>
          <DialogDescription>
            {count.isError
              ? "Unread count is temporarily unavailable. You can still check your inbox below."
              : "Updates and activity for you."}
          </DialogDescription>
        </DialogHeader>
        {open && <NotificationPanel viewerId={viewerId} pinned={pinned} />}
      </ScrollableDialogContent>
    </Dialog>
  );
}

function NotificationPanel({
  viewerId,
  pinned,
}: {
  viewerId: string;
  pinned: Pinned;
}) {
  const [tab, setTab] = useState("inbox");
  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setTab(String(value))}
      className="min-h-0 min-w-0"
    >
      <TabsList>
        <TabsTrigger value="inbox">Inbox</TabsTrigger>
        <TabsTrigger value="preferences">Preferences</TabsTrigger>
      </TabsList>
      <DialogBody>
        <TabsContent value="inbox">
          {tab === "inbox" && (
            <InboxPanel viewerId={viewerId} pinned={pinned} />
          )}
        </TabsContent>
        <TabsContent value="preferences">
          {tab === "preferences" && (
            <PreferencesPanel viewerId={viewerId} pinned={pinned} />
          )}
        </TabsContent>
      </DialogBody>
    </Tabs>
  );
}

function InboxPanel({
  viewerId,
  pinned,
}: {
  viewerId: string;
  pinned: Pinned;
}) {
  const utils = trpc.useUtils();
  const [filter, setFilter] = useState<"open" | "unread" | "archived">("open");
  const [cursors, setCursors] = useState<(Cursor | undefined)[]>([undefined]);
  const input = { viewerId, list: { filter, cursor: cursors.at(-1) } };
  const query = trpc.getNotificationInbox.useQuery(input, {
    ...pinned,
    refetchInterval: 15000,
  });
  const mutation = trpc.setNotificationState.useMutation(pinned);
  const bulkMutation = trpc.markAllNotificationsRead.useMutation(pinned);
  const action = useMutationAction({
    name: "notification-state",
    key: (value: {
      id: string;
      state: { read?: boolean; archived?: boolean };
    }) => `${viewerId}:${value.id}`,
    mutate: async (value) => {
      await mutation.mutateAsync({ viewerId, ...value });
      await utils.getNotificationInbox.invalidate();
    },
    confirmWith: () => [
      confirmQuery(utils.getNotificationInbox, input, pinned),
      confirmQuery(utils.getNotificationUnreadCount, { viewerId }, pinned),
    ],
    errorLabel: "Update notification",
  });
  const bulk = useMutationAction({
    name: "notification-read-all",
    key: () => viewerId,
    mutate: async (readThrough: string) => {
      await bulkMutation.mutateAsync({ viewerId, readThrough });
      await utils.getNotificationInbox.invalidate();
    },
    confirmWith: () => [
      confirmQuery(utils.getNotificationInbox, input, pinned),
      confirmQuery(utils.getNotificationUnreadCount, { viewerId }, pinned),
    ],
    errorLabel: "Mark notifications read",
  });

  async function openItem(item: InboxItem) {
    const outcome = await action.run({ id: item.id, state: { read: true } });

    if (outcome.ok && item.href) {
      window.location.assign(item.href);
    }
  }

  return (
    <div className="flex flex-col gap-4 pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ToggleGroup
          value={[filter]}
          onValueChange={(values) => {
            const value = values[0];

            if (
              value === "open" ||
              value === "unread" ||
              value === "archived"
            ) {
              setFilter(value);
              setCursors([undefined]);
            }
          }}
          aria-label="Notification view"
          className="rounded-md bg-surface-elevated p-1"
        >
          {(["open", "unread", "archived"] as const).map((value) => (
            <ToggleGroupItem
              key={value}
              value={value}
              size="sm"
              className="px-2 pointer-coarse:min-h-11 data-pressed:bg-surface-base data-pressed:text-text-primary motion-reduce:transition-none"
            >
              {value === "open"
                ? "Open"
                : value === "unread"
                  ? "Unread"
                  : "Archived"}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Button
          variant="quiet"
          size="sm"
          disabled={!query.data || bulk.isPending(viewerId)}
          onClick={() => {
            if (query.data) {
              void bulk.run(query.data.readThrough);
            }
          }}
        >
          Mark all read
        </Button>
      </div>
      <QueryContent
        query={query}
        scope="panel"
        loadingMessage="Loading notifications"
        errorMessage="Could not load notifications."
      >
        {(data) => (
          <NotificationInbox
            items={data.items}
            pending={(id) => action.isPending(`${viewerId}:${id}`)}
            onStateChange={(id, state) => void action.run({ id, state })}
            onOpen={(item) => void openItem(item)}
            hasMore={data.nextCursor !== null}
            onLoadMore={() => {
              if (data.nextCursor) {
                setCursors([...cursors, data.nextCursor]);
              }
            }}
          />
        )}
      </QueryContent>
      {cursors.length > 1 && (
        <Button
          variant="outline"
          onClick={() => setCursors(cursors.slice(0, -1))}
        >
          Previous page
        </Button>
      )}
    </div>
  );
}

export function PreferencesPanel({
  viewerId,
  pinned,
}: {
  viewerId: string;
  pinned: Pinned;
}) {
  const utils = trpc.useUtils();
  const query = trpc.getNotificationPreferences.useQuery({ viewerId }, pinned);
  const mutation = trpc.setNotificationPreferences.useMutation(pinned);
  const action = useMutationAction({
    name: "notification-preferences",
    key: (input: {
      type: string;
      values: { inApp: boolean; email: boolean };
    }) => `${viewerId}:${input.type}`,
    mutate: (input) => mutation.mutateAsync({ viewerId, ...input }),
    confirmWith: () => [
      confirmQuery(utils.getNotificationPreferences, { viewerId }, pinned),
    ],
    errorLabel: "Save preferences",
  });
  return (
    <QueryContent
      query={query}
      scope="panel"
      loadingMessage="Loading notification preferences"
      errorMessage="Could not load preferences."
    >
      {(items) => (
        <NotificationPreferences
          items={items}
          pending={(type) => action.isPending(`${viewerId}:${type}`)}
          onChange={(type, channel, enabled) => {
            const item = items.find((candidate) => candidate.type === type);

            if (item) {
              void action.run({
                type,
                values: { ...item.values, [channel]: enabled },
              });
            }
          }}
        />
      )}
    </QueryContent>
  );
}
